/*
Copyright© Celligence International, LLC - 2024

This software program is protected by copyright laws and is the property of
Celligence International, LLC (Celligence). Any unauthorized reproduction,
distribution, modification, or public display of this program, without the
explicit permission of Celligence is strictly prohibited. If you have gained

access to this program without the expressed approval of Celligence report
the incident to legal@celligence.com.
*/

import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { getEpochAsYYYYMMDD, isStrEmptyOrNull, monthDiff } from '../common/UtilityFunctions';
import { useLoanContext } from './LoanContext';
import useLoanImpoundTotal from '../common/hooks/useLoanImpoundTotal';
import useLoanPayment from '../common/hooks/useLoanPayment';
import { BatchEndpoint, HandleChangeType, Status, useBatchEditable } from '../common/hooks/useBatchEditable';
import useGetServicingInstallments, { UseGetServicingInstallments } from '../common/hooks/useGetServicingInstallments';
import useEventDateList from '../common/hooks/useEventDateList';
import { useAuthContext } from './AuthContext';

type EscrowPaymentScheduleContextType = {
  loading: boolean;
  installments: BatchInstallmentResp;
  loanImpounds: BatchImpoundResp;
  loanPayments: BatchPaymentResp;
  hasChanges: boolean;
  save: () => Promise<void>;
  nextEscrowDayYYYYMMDD: string;
  currentEscrowPeriod: number;
  endCurrEscrowPeriod: number;
  cancel: () => void;
  loanImpoundTotals: LoanImpoundTotal[];
  updatePayment: (duedate: number, shortage: number) => void;
  isNewImpoundApplicable: () => boolean;
  nextEscrowInstallment: UseGetServicingInstallments;
  isShortagePaid: () => 'N/A' | 'Yes' | 'No';
  isPaymentEditable: (payment: LoanPayment) => boolean;
  isImpoundEditable: (imp: LoanImpoundTotal) => boolean;
  showRow: (epoch: number) => boolean;
  monthAfterLpi: Date;
  hideTwoYearsPrior: boolean;
  setHideTwoYearsPrior: React.Dispatch<React.SetStateAction<boolean>>;
  isNextEscrowPaymentEditable: () => boolean;
  origNextEscrowDayLoanPayment: LoanPayment;
  updateImpound: (epochdate: number, amt: number) => void;
};

type BatchImpoundResp = {
  [key: string]: LoanImpoundTotal;
};

type BatchPaymentResp = {
  [key: string]: LoanPayment;
};

type BatchInstallmentResp = {
  [key: string]: UseGetServicingInstallments;
};

const EscrowPaymentScheduleContext = createContext<EscrowPaymentScheduleContextType | undefined>(undefined);

const useEscrowPaymentScheduleContext = () => {
  const escrowPaymentScheduleContext = useContext(EscrowPaymentScheduleContext);
  if (escrowPaymentScheduleContext === undefined) {
    /// throwing this error helps in development when the context is out of scope.
    throw new Error('escrowPaymentScheduleContext must be inside a EscrowPaymentScheduleProvider');
  }
  return escrowPaymentScheduleContext;
};

const EscrowPaymentScheduleProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { loanImpoundTotal, loadingLoanImpoundTotal, reloadLoanImpoundTotal } = useLoanImpoundTotal();
  const { loanPayments: origLoanPayments, loadingLoanPayment, reloadLoanPayment } = useLoanPayment();
  const { loanNumber, loadingLoanData, lpi } = useLoanContext();
  const { fullAccessSuperUser } = useAuthContext();

  const { events: aerevents } = useEventDateList('AER');

  const getRemovedDuplicatesEvents = useCallback((events: EventObj[]): EventObj[] => {
    const removedDuplicates = events.reduce((accumulator: EventObj[], current) => {
      let exists = accumulator.find((item) => {
        return getEpochAsYYYYMMDD(item.activeDate) === getEpochAsYYYYMMDD(current.activeDate);
      });
      if (!exists) {
        accumulator = accumulator.concat(current);
      } else {
        if (exists.timestamp < current.timestamp) {
          accumulator = accumulator.filter((item) => item.recId != exists.recId);
          accumulator = accumulator.concat(current);
        }
      }
      return accumulator;
    }, []);
    return removedDuplicates;
  }, []);

  const filteredDuplicateAEREvents = useMemo(() => getRemovedDuplicatesEvents(aerevents), [aerevents]);
  const filteredSortedAEREvents = useMemo(
    () => filteredDuplicateAEREvents.sort((a, b) => a.activeDate - b.activeDate),
    [filteredDuplicateAEREvents],
  );

  let lpiDate = lpi ? new Date(lpi * 1000) : null;
  let lpiYear = lpiDate ? lpiDate.getFullYear() : null;
  let lpiMonth = lpiDate ? lpiDate.getMonth() + 1 : null;
  let lpiDay = 1;

  const currentEscrowPeriod: number = useMemo(() => {
    if (!Array.isArray(loanImpoundTotal) || lpi == null) return null;
    if (!loanImpoundTotal[0]) return null;
    return loanImpoundTotal.reduce((currentEscPeriod: number, curr: LoanImpoundTotal) => {
      if (curr.effectPmtDte <= lpi && curr.effectPmtDte > currentEscPeriod) {
        return curr.effectPmtDte;
      }
      return currentEscPeriod;
    }, loanImpoundTotal[0].effectPmtDte);
  }, [loanImpoundTotal, lpi]);

  const nextPeriod = useMemo(() => {
    if (currentEscrowPeriod == null) return null;
    let nextPeriod = new Date(currentEscrowPeriod * 1000);
    nextPeriod.setFullYear(nextPeriod.getFullYear() + 1);
    nextPeriod.setHours(nextPeriod.getHours() - 24);
    return nextPeriod;
  }, [currentEscrowPeriod]);

  const endCurrEscrowPeriod = nextPeriod == null ? null : nextPeriod.getTime();

  //Determines and formats the lpi date and the next escrow date to use in endpoint path(s)
  const nextEscrowDayYYYYMMDD = useMemo(() => {
    if (lpiDate == null) return '';
    let nextEscrowYear = lpiYear;
    let nextEscrowMonth = lpiMonth + 1;
    let nextEscrowDay = lpiDay;

    if (nextEscrowMonth === 13) {
      nextEscrowYear++;
      nextEscrowMonth = 1;
    }

    return `${nextEscrowYear.toString()}${nextEscrowMonth.toString().padStart(2, '0')}${nextEscrowDay
      .toString()
      .padStart(2, '0')}`;
  }, [lpiYear, lpiMonth, lpiDay, lpiDate]);

  const { loanPayment: unknownOrigNextEscrowDayLoanPayment, loadingLoanPayment: loadingNextEscrowLoanPayment } =
    useLoanPayment(nextEscrowDayYYYYMMDD);
  const origNextEscrowDayLoanPayment = unknownOrigNextEscrowDayLoanPayment
    ? (unknownOrigNextEscrowDayLoanPayment as LoanPayment)
    : null;

  const { servInstData: nextEscrowInstallment, reloadServInstData } = useGetServicingInstallments(
    loanNumber,
    nextEscrowDayYYYYMMDD,
  );

  const lpiYYYYMMDD = useMemo(() => {
    if (lpiDate == null) return '';
    return `${lpiYear.toString()}${lpiMonth.toString().padStart(2, '0')}${lpiDay.toString().padStart(2, '0')}`;
  }, [lpiYear, lpiMonth, lpiDay]);

  const notReady =
    !loanNumber ||
    loadingLoanData ||
    loadingLoanImpoundTotal ||
    loadingLoanPayment ||
    !origLoanPayments ||
    !lpi ||
    isStrEmptyOrNull(nextEscrowDayYYYYMMDD) ||
    loadingNextEscrowLoanPayment ||
    !Array.isArray(loanImpoundTotal);

  const isNewImpoundApplicable = useCallback(() => {
    if (notReady) return false;
    if (loanImpoundTotal.find((imp) => getEpochAsYYYYMMDD(imp.effectPmtDte) == nextEscrowDayYYYYMMDD)) return false;
    return true;
  }, [notReady, nextEscrowDayYYYYMMDD, loanImpoundTotal]);

  const isNewPaymentApplicable = useCallback(() => {
    if (notReady) return false;
    if (origLoanPayments.find((pay) => getEpochAsYYYYMMDD(pay.dueDate) == nextEscrowDayYYYYMMDD)) return false;
    return true;
  }, [notReady, nextEscrowDayYYYYMMDD, origLoanPayments]);

  const getBatchImpoundsObj = useCallback(() => {
    if (notReady) return null;
    const batchEndpoints: BatchEndpoint[] = [];
    loanImpoundTotal.forEach((imp: LoanImpoundTotal) => {
      batchEndpoints.push({
        endpointName: getEpochAsYYYYMMDD(imp.effectPmtDte),
        path:
          process.env.REACT_APP_IMPOUND_TOTAL.replace('live/', '').replace('?', loanNumber) +
          getEpochAsYYYYMMDD(imp.effectPmtDte),
        requestTypes: ['GET', 'POST'],
      });
    });
    if (isNewImpoundApplicable()) {
      batchEndpoints.push({
        endpointName: nextEscrowDayYYYYMMDD,
        path: process.env.REACT_APP_IMPOUND_TOTAL.replace('live/', '').replace('?', loanNumber) + nextEscrowDayYYYYMMDD,
        requestTypes: ['GET', 'POST'],
      });
    }
    return batchEndpoints;
  }, [loanNumber, loanImpoundTotal, nextEscrowDayYYYYMMDD, isNewImpoundApplicable, notReady]);

  const {
    curr: loanImpounds,
    handleChange: handleLoanImpoundChange,
    saveChangesToBackend: saveLoanImpounds,
    hasChanges: hasLoanImpoundsChanges,
    status: loanImpoundsStatus,
  } = useBatchEditable<BatchImpoundResp>({
    batchEndpoints: getBatchImpoundsObj(),
  });

  const getBatchPaymentsObj = useCallback(() => {
    if (notReady) return null;
    var batchEndpoints: BatchEndpoint[] = [];
    loanImpoundTotal.forEach((imp: LoanImpoundTotal) => {
      batchEndpoints.push({
        endpointName: getEpochAsYYYYMMDD(imp.effectPmtDte),
        path:
          process.env.REACT_APP_PAYMENT_DUEDATE.replace('live/', '').replace('?', loanNumber) +
          getEpochAsYYYYMMDD(imp.effectPmtDte),
        requestTypes: ['GET', 'POST'],
      });
    });
    if (isNewPaymentApplicable()) {
      batchEndpoints.push({
        endpointName: nextEscrowDayYYYYMMDD,
        path: process.env.REACT_APP_PAYMENT_INIT.replace('live/', '').replace('?', loanNumber) + nextEscrowDayYYYYMMDD,
        requestTypes: ['POST'],
      });
    } else if (isNewImpoundApplicable()) {
      batchEndpoints.push({
        endpointName: nextEscrowDayYYYYMMDD,
        path:
          process.env.REACT_APP_PAYMENT_DUEDATE.replace('live/', '').replace('?', loanNumber) + nextEscrowDayYYYYMMDD,
        requestTypes: ['GET', 'POST'],
      });
    }
    return batchEndpoints;
  }, [loanNumber, loanImpoundTotal, isNewPaymentApplicable, nextEscrowDayYYYYMMDD, notReady, isNewImpoundApplicable]);

  const {
    curr: loanPayments,
    handleChange: handleLoanPaymentChange,
    saveChangesToBackend: saveLoanPayments,
    hasChanges: hasLoanPaymentsChanges,
    status: loanPaymentsStatus,
  } = useBatchEditable<BatchPaymentResp>({
    batchEndpoints: getBatchPaymentsObj(),
  });

  const getBatchInstallmentsObj = useCallback(() => {
    if (notReady) return null;
    const batchEndpoints: BatchEndpoint[] = [];
    loanImpoundTotal.forEach((imp: LoanImpoundTotal) => {
      batchEndpoints.push({
        endpointName: getEpochAsYYYYMMDD(imp.effectPmtDte),
        path:
          process.env.REACT_APP_INSTALLMENTS_DUEDATE.replace('live/', '').replace('?', loanNumber) +
          getEpochAsYYYYMMDD(imp.effectPmtDte),
        requestTypes: ['GET'],
      });
    });

    return batchEndpoints;
  }, [loanNumber, loanImpoundTotal, notReady]);

  const {
    curr: installments,
    status: installmentsStatus,
    refetch: refetchInstallments,
  } = useBatchEditable<BatchInstallmentResp>({
    batchEndpoints: getBatchInstallmentsObj(),
  });

  const loanImpoundTotals = Array.isArray(loanImpoundTotal) ? loanImpoundTotal : [];

  const updatePayment = useCallback(
    (duedate: number, shortage: number) => {
      const duedateYYYYMMDD = getEpochAsYYYYMMDD(duedate);
      const loanpayment = loanPayments[duedateYYYYMMDD];
      const origPayment = origLoanPayments.find((pay) => getEpochAsYYYYMMDD(pay.dueDate) == duedateYYYYMMDD);

      const paymentActive = loanpayment && loanpayment.active;
      const newPayment = duedateYYYYMMDD == nextEscrowDayYYYYMMDD && isNewPaymentApplicable();

      if (isNaN(shortage)) {
        shortage = 0;
      }

      // cancel any changes if the shortage is the same as the original value
      if (
        (paymentActive && shortage == origPayment?.escrowShortageMonthlyRepaymentAmt) ||
        (newPayment && shortage == origNextEscrowDayLoanPayment?.escrowShortageMonthlyRepaymentAmt)
      ) {
        handleLoanPaymentChange('cancel', duedateYYYYMMDD + '.escrowShortageMonthlyRepaymentAmt');
        handleLoanPaymentChange('cancel', duedateYYYYMMDD + '.defaultEscrowShortageMonthlyRepaymentAmt');
      } else if (paymentActive || newPayment) {
        handleLoanPaymentChange('edit', duedateYYYYMMDD + '.escrowShortageMonthlyRepaymentAmt', shortage);
        handleLoanPaymentChange('edit', duedateYYYYMMDD + '.defaultEscrowShortageMonthlyRepaymentAmt', false);
      }
    },
    [
      handleLoanPaymentChange,
      loanPayments,
      isNewPaymentApplicable,
      nextEscrowDayYYYYMMDD,
      origLoanPayments,
      origNextEscrowDayLoanPayment,
    ],
  );

  const updateImpound = useCallback(
    (epochdate: number, amt: number) => {
      const dateYYYYMMDD = getEpochAsYYYYMMDD(epochdate);
      if (isNaN(amt)) {
        amt = 0;
      }
      if (
        dateYYYYMMDD == nextEscrowDayYYYYMMDD &&
        isNewImpoundApplicable() &&
        amt == loanImpounds[nextEscrowDayYYYYMMDD].mnthlyImpAmt
      ) {
        handleLoanImpoundChange('cancel', dateYYYYMMDD + '.status');
        handleLoanImpoundChange('cancel', dateYYYYMMDD + '.mnthlyImpAmt');
      } else {
        handleLoanImpoundChange('edit', dateYYYYMMDD + '.status', true);
        handleLoanImpoundChange('edit', dateYYYYMMDD + '.mnthlyImpAmt', amt);
      }
    },
    [handleLoanImpoundChange, nextEscrowDayYYYYMMDD, isNewImpoundApplicable, loanImpounds],
  );

  const loading =
    loanPaymentsStatus == Status.LOADING ||
    loanImpoundsStatus == Status.LOADING ||
    installmentsStatus == Status.LOADING ||
    notReady;

  const hasChanges = hasLoanImpoundsChanges || hasLoanPaymentsChanges;

  const save = useCallback(async () => {
    const promises: Promise<void>[] = [];
    promises.push(saveLoanPayments());

    await Promise.all(promises).then(async () => {
      await saveLoanImpounds().then(() => {
        refetchInstallments();
        reloadLoanImpoundTotal();
        reloadServInstData();
        reloadLoanPayment();
      });
    });
  }, [
    saveLoanPayments,
    saveLoanImpounds,
    refetchInstallments,
    reloadLoanImpoundTotal,
    reloadServInstData,
    reloadLoanPayment,
  ]);

  const cancel = useCallback(() => {
    handleLoanPaymentChange('cancel');
    handleLoanImpoundChange('cancel');
  }, [handleLoanPaymentChange, handleLoanImpoundChange]);

  const [hideTwoYearsPrior, setHideTwoYearsPrior] = useState(true);

  const monthAfterLpi = useMemo(() => {
    if (!lpi) return null;
    var monthafter = new Date(lpi * 1000);
    monthafter.setMonth(monthafter.getMonth() + 1);
    return monthafter;
  }, [lpi]);

  const getEarlierOfLpiOrCurrentDate = useCallback(() => {
    const currdate = new Date();
    if (!lpi) return currdate;
    const lpidate = new Date(lpi * 1000);
    if (lpidate < currdate) return lpidate;
    return currdate;
  }, [lpi]);

  const getTwoYearsPrior = useCallback(() => {
    const basedate = getEarlierOfLpiOrCurrentDate();
    var twoyearsprior = new Date(basedate);
    twoyearsprior.setFullYear(twoyearsprior.getFullYear() - 2);
    return twoyearsprior;
  }, [getEarlierOfLpiOrCurrentDate]);

  const showRow = useCallback(
    (epoch: number) => {
      const twoyearsprior = getTwoYearsPrior();
      if (hideTwoYearsPrior) {
        const datetocheckdate = new Date(epoch * 1000);
        if (datetocheckdate < twoyearsprior) return false;
      }
      return true;
    },
    [getTwoYearsPrior, hideTwoYearsPrior],
  );

  const isAfterLpi = useCallback(
    (timestamp: number) => {
      if (!lpi) return false;
      if (!timestamp) return false;
      const lpiyyyymmdd = getEpochAsYYYYMMDD(lpi);
      const epochyyyymmdd = getEpochAsYYYYMMDD(timestamp);
      if (epochyyyymmdd > lpiyyyymmdd) return true;
      return false;
    },
    [lpi],
  );

  const aerFoundBeforeOrOnPaymentDueDate = useCallback(
    (duedate: number) => {
      const aerexists = filteredSortedAEREvents.find(
        (event) => getEpochAsYYYYMMDD(event.activeDate) <= getEpochAsYYYYMMDD(duedate),
      );
      if (aerexists) return true;
      return false;
    },
    [filteredSortedAEREvents],
  );

  const isImpoundEditable = useCallback(
    (imp: LoanImpoundTotal) => {
      return fullAccessSuperUser && isAfterLpi(imp.effectPmtDte);
    },
    [fullAccessSuperUser, isAfterLpi],
  );

  const isPaymentEditable = useCallback(
    (payment: LoanPayment) => {
      return (
        fullAccessSuperUser &&
        isAfterLpi(payment.dueDate) &&
        aerFoundBeforeOrOnPaymentDueDate(payment.dueDate) &&
        payment.active
      );
    },
    [fullAccessSuperUser, isAfterLpi, aerFoundBeforeOrOnPaymentDueDate],
  );

  const isNextEscrowPaymentEditable = useCallback(() => {
    if (notReady) return false;
    if (fullAccessSuperUser && aerFoundBeforeOrOnPaymentDueDate(monthAfterLpi.getTime() / 1000)) return true;
    return false;
  }, [notReady, fullAccessSuperUser, aerFoundBeforeOrOnPaymentDueDate, monthAfterLpi]);

  const isShortagePaid = useCallback(() => {
    if (!loanPayments) return 'N/A';
    if (isStrEmptyOrNull(lpiYYYYMMDD)) return 'N/A';
    if (!loanPayments[lpiYYYYMMDD]) return 'N/A';
    return loanPayments[lpiYYYYMMDD].escrowShortageMonthlyRepaymentAmt == 0 ? 'Yes' : 'No';
  }, [loanPayments, lpiYYYYMMDD]);

  return (
    <EscrowPaymentScheduleContext.Provider
      value={{
        loading,
        installments,
        loanImpounds,
        loanPayments,
        hasChanges,
        save,
        nextEscrowDayYYYYMMDD,
        currentEscrowPeriod,
        endCurrEscrowPeriod,
        cancel,
        loanImpoundTotals,
        updatePayment,
        isNewImpoundApplicable,
        nextEscrowInstallment,
        isShortagePaid,
        isPaymentEditable,
        isImpoundEditable,
        showRow,
        monthAfterLpi,
        hideTwoYearsPrior,
        setHideTwoYearsPrior,
        isNextEscrowPaymentEditable,
        origNextEscrowDayLoanPayment,
        updateImpound,
      }}
    >
      {children}
    </EscrowPaymentScheduleContext.Provider>
  );
};

export { EscrowPaymentScheduleProvider, useEscrowPaymentScheduleContext };
